import React, { useRef, useEffect, useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { TextStyle, FONT_FAMILIES } from '../../pages/template-editor/textEditorTypes';
import {
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Type,
  Trash2,
  ChevronUp,
  ChevronDown,
  RotateCw,
  Sparkles,
  Cloud,
  Keyboard,
  ArrowRight,
  ArrowUp,
  Maximize2,
  Eye,
  Activity,
  Zap,
  Droplets,
  CircleSlash,
  ChevronsLeftRight,
  ChevronsUpDown,
  Palette,
  Repeat,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { UniversalColorPicker } from '../shared/UniversalColorPicker';
import { Database } from 'lucide-react';

interface TextPropertiesPanelProps {
  selectedTextId: string | null;
  textStyle: Partial<TextStyle>;
  dimensions?: { left: number; top: number; width: number; height: number; rotation: number };
  onStyleChange: (style: Partial<TextStyle>, commit?: boolean) => void;
  onDimensionsChange?: (dims: Partial<{ left: number; top: number; width: number; height: number; rotation: number }>) => void;
  onDimensionsCommit?: () => void;
  onClose?: () => void;
  onDelete?: (id: string) => void;
  onAutoMapAll?: (apiId: string, activeLayerBindingOverride?: any) => void;
  layerName?: string;
  onOpenDataBindingSidepane?: () => void;
}

const PropertyInput: React.FC<{
  label?: string;
  value: number;
  onChange: (val: number, commit?: boolean) => void;
  width?: string;
  unit?: string;
  step?: number;
  min?: number;
  onBlur?: () => void;
}> = ({ label, value, onChange, width = "w-9", unit, step = 1, min, onBlur }) => {
  const [localValue, setLocalValue] = useState(value.toString());

  // Keep local value in sync with prop updates (from sliders or external)
  useEffect(() => {
    setLocalValue(value.toString());
  }, [value]);

  const handleCommit = (valStr: string) => {
    const val = parseFloat(valStr);
    if (!isNaN(val)) {
      onChange(val, true);
      onBlur?.();
    } else {
      setLocalValue(value.toString());
    }
  };

  return (
    <div className="flex items-center h-7 px-2 rounded-lg bg-gray-50 border border-transparent hover:border-gray-200 focus-within:border-[#30A84B] focus-within:bg-white focus-within:shadow-[0_2px_4px_rgba(0,0,0,0.02)] transition-all group relative">
      {label && (
        <span className="text-[9px] font-black text-gray-400 mr-1.5 select-none uppercase tracking-widest group-focus-within:text-[#30A84B]">
          {label}
        </span>
      )}
      <input
        type="text"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={(e) => handleCommit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleCommit((e.target as HTMLInputElement).value);
        }}
        className={`${width} bg-transparent text-[11px] font-bold text-gray-700 outline-none text-left pl-0.5`}
      />
      {unit && <span className="text-[10px] text-gray-400 font-bold ml-0.5">{unit}</span>}
      <div className="flex flex-col ml-1 opacity-0 group-hover:opacity-100 transition-all duration-200">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onChange(value + step, true); }}
          className="hover:bg-gray-200 rounded-sm p-0.5 text-gray-400 hover:text-gray-900 transition-colors"
        >
          <ChevronUp className="w-2.5 h-2.5" />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onChange(min !== undefined ? Math.max(min, value - step) : value - step, true); }}
          className="hover:bg-gray-200 rounded-sm p-0.5 text-gray-400 hover:text-gray-900 transition-colors"
        >
          <ChevronDown className="w-2.5 h-2.5" />
        </button>
      </div>
    </div>
  );
};

const ANIMATION_OPTIONS = [
  { id: 'none', label: 'None', icon: CircleSlash, previewClass: '' },
  { id: 'fade', label: 'Fade', icon: Cloud, previewClass: 'preview-animation-fade' },
  { id: 'typewriter', label: 'Type', icon: Keyboard, previewClass: 'preview-animation-typewriter' },
  { id: 'slideIn', label: 'Slide', icon: ArrowRight, previewClass: 'preview-animation-slideIn' },
  { id: 'rise', label: 'Rise', icon: ArrowUp, previewClass: 'preview-animation-rise' },
  { id: 'pop', label: 'Pop', icon: Maximize2, previewClass: 'preview-animation-pop' },
  { id: 'reveal', label: 'Reveal', icon: Eye, previewClass: 'preview-animation-reveal' },
  { id: 'pulse', label: 'Pulse', icon: Activity, previewClass: 'preview-animation-pulse' },
  { id: 'wiggle', label: 'Wiggle', icon: Zap, previewClass: 'preview-animation-wiggle' },
  { id: 'blurIn', label: 'Blur', icon: Droplets, previewClass: 'preview-animation-blurIn' },
];

const EFFECT_OPTIONS = [
  { id: 'none', label: 'None', icon: CircleSlash },
  { id: 'shadow', label: 'Shadow', icon: Type },
  { id: 'lift', label: 'Lift', icon: Type },
  { id: 'hollow', label: 'Hollow', icon: Type },
  { id: 'outline', label: 'Outline', icon: Type },
  { id: 'neon', label: 'Neon', icon: Sparkles },
  { id: 'background', label: 'Background', icon: Palette },
];

export function TextPropertiesPanel({
  selectedTextId,
  textStyle,
  dimensions,
  onStyleChange,
  onDimensionsChange,
  onDimensionsCommit,
  onDelete,
  onOpenDataBindingSidepane
}: TextPropertiesPanelProps) {
  const updateDimension = (key: string, value: number) => {
    if (onDimensionsChange) {
      onDimensionsChange({ [key]: value });
    }
  };

  const [isFontDropdownOpen, setIsFontDropdownOpen] = useState(false);
  const [isPresetsDropdownOpen, setIsPresetsDropdownOpen] = useState(false);
  const [isSpacingDropdownOpen, setIsSpacingDropdownOpen] = useState(false);
  const [activePresetTab, setActivePresetTab] = useState<'effects' | 'animation'>('effects');
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [presetsDropdownPos, setPresetsDropdownPos] = useState({ top: 0, left: 0 });
  const [spacingDropdownPos, setSpacingDropdownPos] = useState({ top: 0, left: 0 });

  const triggerRef = useRef<HTMLButtonElement>(null);
  const presetsTriggerRef = useRef<HTMLButtonElement>(null);
  const spacingTriggerRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    if (isFontDropdownOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 8, left: rect.left });
    }
  }, [isFontDropdownOpen]);

  useLayoutEffect(() => {
    if (isPresetsDropdownOpen && presetsTriggerRef.current) {
      const rect = presetsTriggerRef.current.getBoundingClientRect();
      setPresetsDropdownPos({ top: rect.bottom + 8, left: rect.left });
    }
  }, [isPresetsDropdownOpen]);

  useLayoutEffect(() => {
    if (isSpacingDropdownOpen && spacingTriggerRef.current) {
      const rect = spacingTriggerRef.current.getBoundingClientRect();
      setSpacingDropdownPos({ top: rect.bottom + 8, left: rect.left - 230 });
    }
  }, [isSpacingDropdownOpen]);


  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (isFontDropdownOpen && !document.getElementById('font-dropdown-portal')?.contains(e.target as Node) && !triggerRef.current?.contains(e.target as Node)) {
        setIsFontDropdownOpen(false);
      }
      if (isPresetsDropdownOpen && !document.getElementById('presets-dropdown-portal')?.contains(e.target as Node) && !presetsTriggerRef.current?.contains(e.target as Node)) {
        setIsPresetsDropdownOpen(false);
      }
      if (isSpacingDropdownOpen && !document.getElementById('spacing-dropdown-portal')?.contains(e.target as Node) && !spacingTriggerRef.current?.contains(e.target as Node)) {
        setIsSpacingDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isFontDropdownOpen, isPresetsDropdownOpen, isSpacingDropdownOpen]);

  const handleFontSelect = (font: string) => {
    onStyleChange({ fontFamily: font }, true);
    setIsFontDropdownOpen(false);
  };

  const updateAnimationSetting = (key: string, value: any, commit = true) => {
    const currentAnim = textStyle.animation || {
      type: 'none',
      duration: 1000,
      delay: 0,
      easing: 'ease',
      loop: false
    };
    onStyleChange({
      animation: {
        ...currentAnim,
        [key]: value
      }
    } as any, commit);
  };

  const handleAnimSelect = (animId: string) => {
    updateAnimationSetting('type', animId);
  };

  const handleEffectSelect = (effectId: string) => {
    onStyleChange({ textEffect: effectId as any }, true);
  };

  return (
    <div
      className="flex flex-col gap-1.5 p-0.5"
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Row 1: Transform & Actions */}
      <div className="flex items-center h-8 px-1 gap-0.5">
      {/* Label */}
      <div className="px-2 text-[10px] font-black text-[#30A84B] uppercase tracking-widest border-r border-gray-100 mr-1 select-none whitespace-nowrap">
        Text
      </div>

      {/* Position Section */}
      {dimensions && (
        <div className="flex items-center gap-1.5 px-2 border-r border-gray-100 mr-1" title="Position">
          <div className="flex items-center gap-1">
            <PropertyInput
              label="X"
              value={dimensions.left || 0}
              onChange={(val) => updateDimension('left', val)}
              onBlur={onDimensionsCommit}
            />
            <PropertyInput
              label="Y"
              value={dimensions.top || 0}
              onChange={(val) => updateDimension('top', val)}
              onBlur={onDimensionsCommit}
            />
          </div>
        </div>
      )}

      {/* Size Section */}
      {dimensions && (
        <div className="flex items-center gap-1.5 px-2 border-r border-gray-100 mr-1" title="Size">
          <Maximize2 className="w-5 h-5 text-gray-400" />
          <div className="flex items-center gap-1">
            <PropertyInput
              label="W"
              value={dimensions.width || 0}
              onChange={(val) => updateDimension('width', val)}
              onBlur={onDimensionsCommit}
              width="w-12"
            />
            <PropertyInput
              label="H"
              value={dimensions.height || 0}
              onChange={(val) => updateDimension('height', val)}
              onBlur={onDimensionsCommit}
              width="w-12"
            />
          </div>
        </div>
      )}

      {/* Rotation Section */}
      {dimensions && (
        <div className="flex items-center gap-1.5 px-2 border-r border-gray-100 mr-1" title="Rotation">
          <RotateCw className="w-5 h-5 text-gray-400" />
          <PropertyInput
            label=""
            value={dimensions.rotation || 0}
            onChange={(val) => updateDimension('rotation', val)}
            onBlur={onDimensionsCommit}
            unit="°"
            width="w-10"
          />
        </div>
      )}
          {/* Actions */}
          <div className="flex items-center ml-auto pl-1 border-l border-gray-100">
            <button
              onClick={() => selectedTextId && onDelete?.(selectedTextId)}
              className="p-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
              title="Delete Layer"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-gray-100/60 mx-2" />

        {/* Row 2: Styling */}
        <div className="flex items-center h-9 px-1 gap-0.5">
          {/* Font Family Selector */}
          <div className="relative">
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setIsFontDropdownOpen(!isFontDropdownOpen)}
          className="flex items-center h-7 px-3 gap-2 hover:bg-gray-100 rounded-md transition-colors group border-r border-gray-100 mr-1"
        >
          <span className="text-[13px] font-semibold text-gray-700 truncate max-w-[120px]" style={{ fontFamily: textStyle.fontFamily || 'Bricolage Grotesque' }}>
            {textStyle.fontFamily || 'Bricolage Grotesque'}
          </span>
          <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${isFontDropdownOpen ? 'rotate-180' : ''}`} />
        </button>

        {createPortal(
          <AnimatePresence>
            {isFontDropdownOpen && (
              <motion.div
                id="font-dropdown-portal"
                initial={{ opacity: 0, y: 4, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 4, scale: 0.98 }}
                className="fixed w-64 max-h-[400px] overflow-y-auto bg-white border border-gray-200 shadow-2xl rounded-lg z-[9999] py-1 scrollbar-thin origin-top-left"
                style={{ top: `${dropdownPos.top}px`, left: `${dropdownPos.left}px`, pointerEvents: 'auto' }}
              >
                {FONT_FAMILIES.map((font) => (
                  <button
                    key={font}
                    type="button"
                    onClick={() => handleFontSelect(font)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 transition-colors text-left group ${textStyle.fontFamily === font ? 'bg-green-50/50' : ''}`}
                  >
                    <span className="text-[15px] text-gray-800" style={{ fontFamily: font }}>{font}</span>
                    {textStyle.fontFamily === font && <div className="w-1.5 h-1.5 rounded-full bg-green-500" />}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
      </div>

      {/* Font Size */}
      <div className="flex items-center h-7 px-1 border-r border-gray-100 mr-1">
        <PropertyInput
          value={textStyle.fontSize || 16}
          onChange={(val) => onStyleChange({ fontSize: val })}
          width="w-7"
          min={1}
        />
      </div>

      {/* Formatting */}
      <div className="flex items-center gap-0.5 px-1 border-r border-gray-100 mr-1">
        <button
          onClick={() => onStyleChange({ fontWeight: textStyle.fontWeight === 'bold' ? 'normal' : 'bold' })}
          className={`p-1.5 rounded-md ${textStyle.fontWeight === 'bold' ? 'bg-green-50 text-green-600' : 'text-gray-500 hover:bg-gray-100'}`}
        ><Bold className="w-5 h-5" /></button>
        <button
          onClick={() => onStyleChange({ italic: !textStyle.italic })}
          className={`p-1.5 rounded-md ${textStyle.italic ? 'bg-green-50 text-green-600' : 'text-gray-500 hover:bg-gray-100'}`}
        ><Italic className="w-5 h-5" /></button>
        <button
          onClick={() => onStyleChange({ underline: !textStyle.underline })}
          className={`p-1.5 rounded-md ${textStyle.underline ? 'bg-green-50 text-green-600' : 'text-gray-500 hover:bg-gray-100'}`}
        ><Underline className="w-5 h-5" /></button>
      </div>

      {/* Alignment */}
      <div className="flex items-center gap-0.5 px-1 border-r border-gray-100 mr-1">
        {[
          { icon: AlignLeft, value: 'left' },
          { icon: AlignCenter, value: 'center' },
          { icon: AlignRight, value: 'right' }
        ].map((item) => (
          <button
            key={item.value}
            onClick={() => onStyleChange({ textAlign: item.value as any })}
            className={`p-1.5 rounded-md ${(textStyle.textAlign || 'left') === item.value ? 'bg-green-50 text-green-600' : 'text-gray-500 hover:bg-gray-100'}`}
          ><item.icon className="w-5 h-5" /></button>
        ))}
      </div>

      {/* Color */}
      <div className="flex items-center gap-2 px-2 border-r border-gray-100 mr-1">
        <div className="relative">
          <UniversalColorPicker
            value={textStyle.fill || { type: 'solid', color: textStyle.color || '#000000' }}
            onChange={(fill, commit) => onStyleChange({ fill, color: fill.color }, commit)}
            label="Text Color"
            id="text-color-popover"
          />

        </div>
      </div>
      {/* Spacing Selector */}
      <div className="flex items-center gap-2 px-2 border-r border-gray-100 mr-1">
        <div className="relative">
          <button
            ref={spacingTriggerRef}
            onClick={() => setIsSpacingDropdownOpen(!isSpacingDropdownOpen)}
            className="flex items-center gap-1.5 h-7 px-2 rounded-md hover:bg-gray-100 transition-colors group"
          >
            <ChevronsLeftRight className="w-5 h-5 text-gray-400" />
            <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${isSpacingDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
 
          {createPortal(
            <AnimatePresence>
              {isSpacingDropdownOpen && (
                <motion.div
                  id="spacing-dropdown-portal"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  className="fixed w-[280px] bg-white border border-gray-200 shadow-2xl rounded-2xl z-[9999] p-4 origin-top-right"
                  style={{ top: `${spacingDropdownPos.top}px`, left: `${spacingDropdownPos.left}px`, pointerEvents: 'auto' }}
                >
                  <div className="space-y-4">
                    <div className="space-y-2.5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <ChevronsLeftRight className="w-5 h-5 text-gray-400" />
                          <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Letter Spacing</span>
                        </div>
                        <PropertyInput value={textStyle.letterSpacing || 0} onChange={(val) => onStyleChange({ letterSpacing: val })} width="w-8" />
                      </div>
                      <input
                        type="range" min="-50" max="100" step="1"
                        defaultValue={textStyle.letterSpacing || 0}
                        onInput={(e) => onStyleChange({ letterSpacing: parseInt((e.target as HTMLInputElement).value) }, false)}
                        onMouseUp={(e) => onStyleChange({ letterSpacing: parseInt((e.target as HTMLInputElement).value) }, true)}
                        className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-[#30A84B]"
                      />
                    </div>
   <div className="space-y-2.5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <ChevronsUpDown className="w-5 h-5 text-gray-400" />
                          <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Line Height</span>
                        </div>
                        <PropertyInput value={textStyle.lineHeight || 1.2} onChange={(val) => onStyleChange({ lineHeight: val }, true)} width="w-8" step={0.1} />
                      </div>
                      <input
                        type="range" min="0.5" max="3.0" step="0.1"
                        defaultValue={textStyle.lineHeight || 1.2}
                        onInput={(e) => onStyleChange({ lineHeight: parseFloat((e.target as HTMLInputElement).value) }, false)}
                        onMouseUp={(e) => onStyleChange({ lineHeight: parseFloat((e.target as HTMLInputElement).value) }, true)}
                        className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-[#30A84B]"
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>,
            document.body
          )}
        </div>
      </div>

      {/* Data Binding Selector */}
      <div className="flex items-center gap-2 px-2 border-r border-gray-100 mr-1">
        <div className="relative">
          <button
            type="button"
            onClick={onOpenDataBindingSidepane}
            className={`flex items-center justify-between gap-1.5 h-7 px-2.5 rounded-md hover:bg-gray-100 transition-colors text-[11px] font-bold uppercase tracking-tighter ${
              (textStyle as any).binding ? 'bg-green-50 text-green-600' : 'text-gray-500'
            }`}
            style={{ minWidth: '85px' }}
            title="Configure Custom API Data Binding"
          >
            <div className="flex items-center gap-1.5 truncate">
              <Database className="w-3.5 h-3.5" />
              <span className="truncate max-w-[65px] font-sans font-bold">
                {(textStyle as any).binding ? (textStyle as any).binding.apiKey.split('.').pop() : 'Static'}
              </span>
            </div>
          </button>
        </div>
      </div>

      {/* Presets (Effects & Animation) Selector */}
      <div className="flex items-center gap-2 px-2 border-r border-gray-100 mr-1">
        <div className="relative">
          <button
            ref={presetsTriggerRef}
            onClick={() => setIsPresetsDropdownOpen(!isPresetsDropdownOpen)}
            className="flex items-center gap-1.5 h-7 px-2 rounded-md hover:bg-gray-100 transition-colors group"
          >
            <Sparkles className={`w-5 h-5 ${(textStyle.textEffect && textStyle.textEffect !== 'none') || (textStyle.animation?.type && textStyle.animation.type !== 'none') ? 'text-green-600' : 'text-gray-400'}`} />
            <span className="text-[12px] font-bold text-gray-700 uppercase tracking-tighter">Presets</span>
            <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${isPresetsDropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {createPortal(
            <AnimatePresence>
              {isPresetsDropdownOpen && (
                <motion.div
                  id="presets-dropdown-portal"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  className="fixed w-[280px] bg-white border border-gray-200 shadow-2xl rounded-2xl z-[9999] overflow-hidden origin-top-left"
                  style={{ top: `${presetsDropdownPos.top}px`, left: `${presetsDropdownPos.left}px`, pointerEvents: 'auto' }}
                >
                  <div className="flex border-b border-gray-100 bg-gray-50/50 p-1">
                    <button onClick={() => setActivePresetTab('effects')} className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-widest rounded-lg ${activePresetTab === 'effects' ? 'bg-white text-[#30A84B] shadow-sm' : 'text-gray-400'}`}>Effects</button>
                    <button onClick={() => setActivePresetTab('animation')} className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-widest rounded-lg ${activePresetTab === 'animation' ? 'bg-white text-[#30A84B] shadow-sm' : 'text-gray-400'}`}>Animate</button>
                  </div>
                  <div className="p-3 max-h-[380px] overflow-y-auto scrollbar-none">
                    {activePresetTab === 'effects' ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-2">
                          {EFFECT_OPTIONS.map((effect) => (
                            <button
                              key={effect.id}
                              onClick={() => {
                                handleEffectSelect(effect.id);
                                if (effect.id === 'background' && !textStyle.backgroundColor) {
                                  onStyleChange({
                                    backgroundColor: '#fbbf24',
                                    backgroundSpread: 12,
                                    backgroundCornerRadius: 8,
                                    backgroundOpacity: 100
                                  });
                                } else if (effect.id === 'shadow' && !textStyle.effectColor) {
                                  onStyleChange({ effectColor: '#000000', effectOffset: 50, effectDirection: 45, effectIntensity: 50 });
                                } else if (effect.id === 'outline' && !textStyle.effectColor) {
                                  onStyleChange({ effectColor: '#000000', effectThickness: 50 });
                                } else if (effect.id === 'neon' && !textStyle.effectIntensity) {
                                  onStyleChange({ effectIntensity: 50 });
                                }
                              }}
                              className={`group flex flex-col items-center justify-center gap-2 p-2.5 rounded-xl border ${textStyle.textEffect === effect.id ? 'border-[#30A84B] bg-[#30A84B]/[0.03]' : 'border-gray-100 hover:bg-gray-50'}`}
                            >
                              <div className={`flex items-center justify-center w-9 h-9 rounded-lg ${textStyle.textEffect === effect.id ? 'bg-[#30A84B] text-white' : 'bg-gray-50 text-gray-400'}`}><effect.icon className="w-5 h-5" /></div>
                              <span className={`text-[10px] font-bold ${textStyle.textEffect === effect.id ? 'text-[#30A84B]' : 'text-gray-500'}`}>{effect.label}</span>
                            </button>
                          ))}
                        </div>

                        {textStyle.textEffect === 'shadow' && (
                          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="pt-4 border-t border-gray-100 space-y-4">
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Color</span>
                              <div className="flex items-center gap-2">
                                <div className="relative group/color cursor-pointer">
                                  <input type="color" className="absolute inset-0 opacity-0 cursor-pointer w-full h-full scale-150" value={textStyle.effectColor || '#000000'} onChange={(e) => onStyleChange({ effectColor: e.target.value })} />
                                  <div className="w-6 h-6 rounded-md border border-gray-200 shadow-sm" style={{ backgroundColor: textStyle.effectColor || '#000000' }} />
                                </div>
                                <span className="text-[10px] font-bold text-gray-700 uppercase tabular-nums">{textStyle.effectColor || '#000000'}</span>
                              </div>
                            </div>
                            <div className="space-y-2.5">
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Offset</span>
                                <PropertyInput value={textStyle.effectOffset ?? 50} onChange={(val) => onStyleChange({ effectOffset: val })} width="w-8" />
                              </div>
                              <input type="range" min="0" max="100" defaultValue={textStyle.effectOffset ?? 50} onInput={(e) => onStyleChange({ effectOffset: parseInt((e.target as HTMLInputElement).value) })} className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-[#30A84B]" />
                            </div>
                            <div className="space-y-2.5">
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Direction</span>
                                <PropertyInput value={textStyle.effectDirection ?? 45} onChange={(val) => onStyleChange({ effectDirection: val })} width="w-8" unit="°" />
                              </div>
                              <input type="range" min="-180" max="180" defaultValue={textStyle.effectDirection ?? 45} onInput={(e) => onStyleChange({ effectDirection: parseInt((e.target as HTMLInputElement).value) })} className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-[#30A84B]" />
                            </div>
                            <div className="space-y-2.5">
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Blur</span>
                                <PropertyInput value={textStyle.effectIntensity ?? 50} onChange={(val) => onStyleChange({ effectIntensity: val })} width="w-8" />
                              </div>
                              <input type="range" min="0" max="100" defaultValue={textStyle.effectIntensity ?? 50} onInput={(e) => onStyleChange({ effectIntensity: parseInt((e.target as HTMLInputElement).value) })} className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-[#30A84B]" />
                            </div>
                          </motion.div>
                        )}

                        {textStyle.textEffect === 'lift' && (
                          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="pt-4 border-t border-gray-100 space-y-4">
                            <div className="space-y-2.5">
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Intensity</span>
                                <PropertyInput value={textStyle.effectIntensity ?? 50} onChange={(val) => onStyleChange({ effectIntensity: val })} width="w-8" />
                              </div>
                              <input type="range" min="0" max="100" defaultValue={textStyle.effectIntensity ?? 50} onInput={(e) => onStyleChange({ effectIntensity: parseInt((e.target as HTMLInputElement).value) })} className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-[#30A84B]" />
                            </div>
                          </motion.div>
                        )}

                        {textStyle.textEffect === 'hollow' && (
                          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="pt-4 border-t border-gray-100 space-y-4">
                            <div className="space-y-2.5">
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Thickness</span>
                                <PropertyInput value={textStyle.effectThickness ?? 50} onChange={(val) => onStyleChange({ effectThickness: val })} width="w-8" />
                              </div>
                              <input type="range" min="1" max="100" defaultValue={textStyle.effectThickness ?? 50} onInput={(e) => onStyleChange({ effectThickness: parseInt((e.target as HTMLInputElement).value) })} className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-[#30A84B]" />
                            </div>
                          </motion.div>
                        )}

                        {textStyle.textEffect === 'outline' && (
                          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="pt-4 border-t border-gray-100 space-y-4">
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Color</span>
                              <div className="flex items-center gap-2">
                                <div className="relative group/color cursor-pointer">
                                  <input type="color" className="absolute inset-0 opacity-0 cursor-pointer w-full h-full scale-150" value={textStyle.effectColor || '#000000'} onChange={(e) => onStyleChange({ effectColor: e.target.value })} />
                                  <div className="w-6 h-6 rounded-md border border-gray-200 shadow-sm" style={{ backgroundColor: textStyle.effectColor || '#000000' }} />
                                </div>
                                <span className="text-[10px] font-bold text-gray-700 uppercase tabular-nums">{textStyle.effectColor || '#000000'}</span>
                              </div>
                            </div>
                            <div className="space-y-2.5">
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Thickness</span>
                                <PropertyInput value={textStyle.effectThickness ?? 50} onChange={(val) => onStyleChange({ effectThickness: val })} width="w-8" />
                              </div>
                              <input type="range" min="1" max="100" defaultValue={textStyle.effectThickness ?? 50} onInput={(e) => onStyleChange({ effectThickness: parseInt((e.target as HTMLInputElement).value) })} className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-[#30A84B]" />
                            </div>
                          </motion.div>
                        )}

                        {textStyle.textEffect === 'neon' && (
                          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="pt-4 border-t border-gray-100 space-y-4">
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Intensity</span>
                              <PropertyInput value={textStyle.effectIntensity ?? 50} onChange={(val) => onStyleChange({ effectIntensity: val })} width="w-8" />
                            </div>
                            <input type="range" min="1" max="100" defaultValue={textStyle.effectIntensity ?? 50} onInput={(e) => onStyleChange({ effectIntensity: parseInt((e.target as HTMLInputElement).value) })} className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-[#30A84B]" />
                          </motion.div>
                        )}

                        {textStyle.textEffect === 'background' && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className="pt-4 border-t border-gray-100 space-y-4"
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Color</span>
                              <div className="flex items-center gap-2">
                                <div className="relative group/color cursor-pointer">
                                  <input
                                    type="color"
                                    className="absolute inset-0 opacity-0 cursor-pointer w-full h-full scale-150"
                                    value={textStyle.backgroundColor || '#fbbf24'}
                                    onChange={(e) => onStyleChange({ backgroundColor: e.target.value })}
                                  />
                                  <div className="w-6 h-6 rounded-md border border-gray-200 shadow-sm transition-transform group-hover/color:scale-110" style={{ backgroundColor: textStyle.backgroundColor || '#fbbf24' }} />
                                </div>
                                <span className="text-[10px] font-bold text-gray-700 uppercase tabular-nums">{textStyle.backgroundColor || '#FBBF24'}</span>
                              </div>
                            </div>

                            <div className="space-y-2.5">
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Spread</span>
                                <PropertyInput
                                  value={textStyle.backgroundSpread ?? 10}
                                  onChange={(val) => onStyleChange({ backgroundSpread: val })}
                                  width="w-8"
                                />
                              </div>
                              <input
                                type="range" min="0" max="100" step="1"
                                defaultValue={textStyle.backgroundSpread ?? 10}
                                onInput={(e) => onStyleChange({ backgroundSpread: parseInt((e.target as HTMLInputElement).value) })}
                                className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-[#30A84B]"
                              />
                            </div>

                            <div className="space-y-2.5">
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Roundness</span>
                                <PropertyInput
                                  value={textStyle.backgroundCornerRadius ?? 8}
                                  onChange={(val) => onStyleChange({ backgroundCornerRadius: val })}
                                  width="w-8"
                                />
                              </div>
                              <input
                                type="range" min="0" max="100" step="1"
                                defaultValue={textStyle.backgroundCornerRadius ?? 8}
                                onInput={(e) => onStyleChange({ backgroundCornerRadius: parseInt((e.target as HTMLInputElement).value) })}
                                className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-[#30A84B]"
                              />
                            </div>

                            <div className="space-y-2.5">
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Opacity</span>
                                <PropertyInput
                                  value={textStyle.backgroundOpacity ?? 100}
                                  onChange={(val) => onStyleChange({ backgroundOpacity: val })}
                                  width="w-8"
                                  unit="%"
                                />
                              </div>
                              <input
                                type="range" min="0" max="100" step="1"
                                defaultValue={textStyle.backgroundOpacity ?? 100}
                                onInput={(e) => onStyleChange({ backgroundOpacity: parseInt((e.target as HTMLInputElement).value) })}
                                className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-[#30A84B]"
                              />
                            </div>
                          </motion.div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-2">
                          {ANIMATION_OPTIONS.map((anim) => (
                            <button key={anim.id} onClick={() => handleAnimSelect(anim.id)} className={`group flex flex-col items-center justify-center gap-2 p-2.5 rounded-xl border ${textStyle.animation?.type === anim.id ? 'border-[#30A84B] bg-[#30A84B]/[0.03]' : 'border-gray-100 hover:bg-gray-50'}`}>
                              <div className={`flex items-center justify-center w-9 h-9 rounded-lg ${textStyle.animation?.type === anim.id ? 'bg-[#30A84B] text-white' : 'bg-gray-50 text-gray-400'}`}><anim.icon className="w-5 h-5" /></div>
                              <span className={`text-[10px] font-bold ${textStyle.animation?.type === anim.id ? 'text-[#30A84B]' : 'text-gray-500'}`}>{anim.label}</span>
                            </button>
                          ))}
                        </div>

                        {textStyle.animation?.type && textStyle.animation.type !== 'none' && (
                          <div className="pt-3 border-t border-gray-100 space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Duration</span>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => updateAnimationSetting('duration', 5000)}
                                  className="text-[9px] font-bold text-gray-400 hover:text-[#30A84B] px-1 bg-gray-50 rounded"
                                  title="Set to 5s"
                                >5s</button>
                                <button
                                  onClick={() => updateAnimationSetting('duration', 10000)}
                                  className="text-[9px] font-bold text-gray-400 hover:text-[#30A84B] px-1 bg-gray-50 rounded"
                                  title="Set to 10s"
                                >10s</button>
                                <PropertyInput
                                  value={textStyle.animation?.duration || 1000}
                                  onChange={(val) => updateAnimationSetting('duration', val)}
                                  width="w-10"
                                  unit="ms"
                                  step={100}
                                  min={0}
                                />
                              </div>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Delay</span>
                              <PropertyInput
                                value={textStyle.animation?.delay || 0}
                                onChange={(val) => updateAnimationSetting('delay', val)}
                                width="w-10"
                                unit="ms"
                                step={100}
                                  min={0}
                                />
                              </div>
                              <div className="flex items-center justify-between pb-1">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Loop</span>
                                <button
                                  onClick={() => updateAnimationSetting('loop', !textStyle.animation?.loop)}
                                  className={`p-1 rounded-md transition-colors ${textStyle.animation?.loop ? 'bg-green-50 text-green-600' : 'text-gray-400 hover:bg-gray-100'}`}
                                  title="Repeat animation"
                                >
                                <Repeat className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Easing</span>
                              <select
                                value={textStyle.animation?.easing || 'ease'}
                                onChange={(e) => updateAnimationSetting('easing', e.target.value)}
                                className="text-[10px] font-bold text-gray-700 bg-gray-50 border-none outline-none rounded-md px-1 py-0.5 h-7 cursor-pointer hover:bg-gray-100"
                              >
                                <option value="ease">Ease</option>
                                <option value="linear">Linear</option>
                                <option value="ease-in">Ease In</option>
                                <option value="ease-out">Ease Out</option>
                                <option value="ease-in-out">Ease In-Out</option>
                                <option value="backOut">Back Out</option>
                                <option value="elasticOut">Elastic Out</option>
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>,
            document.body
          )}
        </div>
      </div>
    </div>
  </div>
);
}

